import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';

/**
 * Date adapter that parses typed input using the SAME field order it displays.
 *
 * Why this exists
 * ---------------
 * Angular Material's stock `NativeDateAdapter.parse()` delegates to
 * `Date.parse()`, which always interprets `03/04/2026` as month-first (4 March)
 * no matter what locale is active. Meanwhile `format()` is locale-aware, and
 * every Spanish locale — including es-US — renders dates day-first.
 *
 * Left alone, that combination is silently destructive: a teacher working in
 * Spanish sees `3/4/2026` in the field, types `3/4/2026` back, and the app
 * stores 4 March. No validation error, no visible symptom — a wrong date of
 * birth or enrollment date written straight to the API.
 *
 * This adapter derives the locale's actual day/month/year order from the Intl
 * data itself (rather than hardcoding a list of locales) and parses to match.
 *
 * `format()` is inherited: NativeDateAdapter already routes it through
 * `Intl.DateTimeFormat` using the display formats in `DRDP_DATE_FORMATS`.
 */
@Injectable()
export class DrdpDateAdapter extends NativeDateAdapter {
  /** ISO `yyyy-mm-dd` is unambiguous and always accepted, whatever the locale. */
  private static readonly ISO = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

  /** Numeric date with `/`, `-` or `.` separators, e.g. 3/4/2026 or 03-04-26. */
  private static readonly NUMERIC = /^(\d{1,4})[/\-.](\d{1,2})[/\-.](\d{1,4})$/;

  /**
   * Derived from the locale rather than hardcoded: en-US and es-US both start on
   * Sunday, but a future non-US locale may start on Monday.
   */
  public override getFirstDayOfWeek(): number {
    // `weekInfo` is the standardised source; not every engine exposes it yet.
    const locale = new Intl.Locale(this.locale as string) as Intl.Locale & {
      weekInfo?: { firstDay: number };
      getWeekInfo?: () => { firstDay: number };
    };

    const firstDay = locale.getWeekInfo?.().firstDay ?? locale.weekInfo?.firstDay;

    // weekInfo uses ISO numbering (1 = Monday .. 7 = Sunday);
    // Material expects 0 = Sunday .. 6 = Saturday.
    if (firstDay) return firstDay % 7;

    // Fallback: English-speaking US convention is Sunday, most others Monday.
    return String(this.locale).startsWith('en') ? 0 : 1;
  }

  public override parse(value: unknown): Date | null {
    if (value == null || value === '') return null;
    if (value instanceof Date) return this.clone(value);
    if (typeof value === 'number') return new Date(value);
    if (typeof value !== 'string') return super.parse(value);

    const text = value.trim();
    if (!text) return null;

    const iso = DrdpDateAdapter.ISO.exec(text);
    if (iso) {
      return this.build(Number(iso[1]), Number(iso[2]), Number(iso[3]));
    }

    const numeric = DrdpDateAdapter.NUMERIC.exec(text);
    if (numeric) {
      const parts = [Number(numeric[1]), Number(numeric[2]), Number(numeric[3])];
      const order = this.fieldOrder();
      return this.build(
        this.expandYear(parts[order.indexOf('year')]),
        parts[order.indexOf('month')],
        parts[order.indexOf('day')]
      );
    }

    // Anything else (e.g. "3 de abril de 2026", "Apr 3 2026") is unambiguous
    // about which number is the day, so the built-in parser is safe.
    return super.parse(text);
  }

  /**
   * The order in which this locale writes day, month and year — read from Intl
   * rather than assumed. en-US yields month,day,year; es-US yields day,month,year.
   */
  private fieldOrder(): Array<'day' | 'month' | 'year'> {
    const parts = new Intl.DateTimeFormat(this.locale as string, {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    }).formatToParts(new Date(2026, 3, 3));

    const order = parts
      .map((part) => part.type)
      .filter((type): type is 'day' | 'month' | 'year' =>
        type === 'day' || type === 'month' || type === 'year'
      );

    // Defensive: if Intl gave us something unexpected, fall back to US order
    // rather than mis-assigning fields.
    return order.length === 3 ? order : ['month', 'day', 'year'];
  }

  /**
   * Resolve a 2-digit year. Dates of birth are common here, so a year that would
   * land in the future is read as the previous century: `95` -> 1995, `26` -> 2026.
   */
  private expandYear(year: number): number {
    if (year >= 100) return year;
    const currentYear = new Date().getFullYear();
    const thisCentury = Math.floor(currentYear / 100) * 100 + year;
    return thisCentury <= currentYear + 5 ? thisCentury : thisCentury - 100;
  }

  /**
   * Build a date, rejecting values that would silently roll over.
   * `31/02/2026` must surface as invalid, not quietly become 3 March.
   *
   * The calendar check has to happen BEFORE `createDate`, which throws on an
   * out-of-range day rather than returning an invalid date. An exception here
   * would escape into Material's input handling on every keystroke while the
   * user is still mid-typing, so we validate first and return `invalid()`.
   */
  private build(year: number, month: number, day: number): Date {
    if (
      !Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day) ||
      month < 1 || month > 12 || day < 1 || day > 31
    ) {
      return this.invalid();
    }

    // Does this day actually exist in this month of this year? (Leap years too.)
    const probe = new Date(year, month - 1, day);
    probe.setFullYear(year); // years < 100 would otherwise map into the 1900s
    if (probe.getMonth() !== month - 1 || probe.getDate() !== day) {
      return this.invalid();
    }

    return this.createDate(year, month - 1, day);
  }
}
